import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { MapPin, Bus, Clock, ArrowRight, Landmark } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHero from "../components/PageHero";
import { useReservation } from "../components/ReservationModal";
import { safranbolu, hotel } from "../mock";

// Inject SEO meta + JSON-LD structured data
const useSafranboluSEO = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "Safranbolu | UNESCO Dünya Mirası Şehri — Safranbolu Çelebi Otel";

    const meta = document.createElement("meta");
    meta.name = "description";
    meta.content =
      "Safranbolu'nun tarihi, Osmanlı konakları, tarihi çarşısı, safranı ve lokumu. UNESCO Dünya Mirası şehrinin hikâyesini Safranbolu Çelebi Otel ile keşfedin.";
    document.head.appendChild(meta);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "TouristDestination",
          name: "Safranbolu",
          description:
            "UNESCO Dünya Mirası Listesi'nde yer alan, Osmanlı sivil mimarisinin en iyi korunmuş örneklerinden biri olan tarihî Türk kenti.",
          touristType: ["Kültür turizmi", "Tarih", "Gastronomi"],
          address: {
            "@type": "PostalAddress",
            addressLocality: "Safranbolu",
            addressRegion: "Karabük",
            addressCountry: "TR",
          },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Ana Sayfa", item: "/" },
            { "@type": "ListItem", position: 2, name: "Safranbolu", item: "/safranbolu" },
          ],
        },
        {
          "@type": "Hotel",
          name: hotel.name,
          telephone: hotel.phones[0],
          address: {
            "@type": "PostalAddress",
            streetAddress: "Atatürk Mah. Sadri Artunç Cad. No: 80",
            addressLocality: "Safranbolu",
            addressRegion: "Karabük",
            addressCountry: "TR",
          },
        },
      ],
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      document.head.removeChild(meta);
      document.head.removeChild(ld);
    };
  }, []);
};

const SectionLabel = ({ eyebrow, title, id, light }) => (
  <>
    <p className="serif-i text-[#b8893a] text-2xl mb-3">{eyebrow}</p>
    <h2
      id={id}
      className={`font-display text-[2.4rem] md:text-5xl font-500 leading-[1.05] ${
        light ? "text-white" : "text-[#2b2620]"
      }`}
    >
      {title}
    </h2>
  </>
);

const Safranbolu = () => {
  const { openReservation } = useReservation();
  useSafranboluSEO();
  const { story, timeline, houses, bazaar, saffron, lokum, features, nearby, transport, credits } = safranbolu;

  return (
    <div className="font-body bg-[#fdfbf7] overflow-hidden">
      <PageHero
        image={safranbolu.hero}
        eyebrow="UNESCO Dünya Mirası · 1994"
        title="Safranbolu"
        subtitle="Zamanın durduğu, tarihin yaşadığı eşsiz şehir."
        height="66vh"
      />

      {/* Intro (kept) */}
      <section aria-labelledby="sb-intro" className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <Reveal>
            <p className="serif-i text-[#b8893a] text-2xl mb-5">Safranın Diyarı</p>
            <h2 id="sb-intro" className="sr-only">Safranbolu Hakkında</h2>
            <p className="font-display text-2xl md:text-[2rem] text-[#2b2620] font-500 leading-[1.6]">
              {safranbolu.intro}
            </p>
            <div className="gold-divider mt-8" />
          </Reveal>
        </div>
      </section>

      {/* Story */}
      <section aria-labelledby="sb-story" className="pb-24 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 relative">
            <Reveal className="clip-reveal">
              <img
                src={story.image}
                alt={story.imageAlt}
                loading="lazy"
                className="w-full h-[420px] md:h-[560px] object-cover"
              />
            </Reveal>
            <div className="absolute -bottom-8 right-4 md:-right-8 bg-[#2b2620] text-white px-7 py-5 hidden md:block">
              <p className="font-display text-4xl text-[#d9b877] font-600 leading-none">614</p>
              <p className="text-[10px] text-white/60 tracking-[0.2em] uppercase mt-2">UNESCO Miras No.</p>
            </div>
          </div>
          <div className="lg:col-span-5">
            <SectionLabel eyebrow={story.eyebrow} title={story.title} id="sb-story" />
            <div className="rule-h max-w-[110px] my-6" />
            {story.paragraphs.map((p, i) => (
              <p key={i} className="text-[#6b6459] leading-[1.95] text-[15px] mb-4">{p}</p>
            ))}
            <div className="flex items-stretch gap-8 mt-8">
              {story.stats.map((s, i) => (
                <div key={i} className="flex items-center gap-8">
                  {i > 0 && <span className="rule-v h-10" />}
                  <div>
                    <p className="font-display text-3xl text-[#2b2620] font-600">{s.n}</p>
                    <p className="text-[11px] text-[#9a9086] mt-1 tracking-wide">{s.l}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section aria-labelledby="sb-timeline" className="py-24 md:py-32 bg-[#f6f0e6]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Reveal className="text-center mb-16 md:mb-20">
            <p className="serif-i text-[#b8893a] text-2xl mb-3">Çağların İzinde</p>
            <h2 id="sb-timeline" className="font-display text-[2.4rem] md:text-5xl text-[#2b2620] font-500">
              Uygarlıklar Zaman Çizelgesi
            </h2>
            <p className="text-[#6b6459] max-w-xl mx-auto mt-5 text-[14px] leading-relaxed">
              MÖ 3000'lerden UNESCO'ya uzanan, kaynaklarla doğrulanmış bir kronoloji.
            </p>
          </Reveal>

          <div className="timeline">
            {timeline.map((t, i) => (
              <Reveal key={i} delay={i * 40}>
                <div className="tl-item group">
                  <span className="tl-dot" />
                  <span className="tl-year block text-2xl md:text-2xl font-600 mb-1 md:mb-0">{t.year}</span>
                  <h3 className="font-display text-2xl text-[#2b2620] font-600 mb-2 group-hover:text-[#b8893a] transition-colors">
                    {t.title}
                  </h3>
                  <p className="text-[14px] text-[#6b6459] leading-[1.85] max-w-xl">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Houses */}
      <section aria-labelledby="sb-houses" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <SectionLabel eyebrow={houses.eyebrow} title={houses.title} id="sb-houses" />
            <div className="rule-h max-w-[110px] my-6" />
            {houses.paragraphs.map((p, i) => (
              <p key={i} className="text-[#6b6459] leading-[1.95] text-[15px] mb-4">{p}</p>
            ))}
          </div>
          <div className="lg:col-span-7 relative">
            <Reveal className="clip-reveal">
              <img src={houses.images[0].src} alt={houses.images[0].alt} loading="lazy"
                className="w-full h-[380px] md:h-[520px] object-cover" />
            </Reveal>
            <Reveal delay={200} className="clip-reveal absolute -bottom-10 -left-6 md:-left-14 w-40 md:w-60 hidden sm:block shadow-2xl">
              <img src={houses.images[1].src} alt={houses.images[1].alt} loading="lazy"
                className="w-full h-44 md:h-64 object-cover border-[6px] border-[#fdfbf7]" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Bazaar & crafts */}
      <section aria-labelledby="sb-bazaar" className="py-24 md:py-28 bg-[#f3ece0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <Reveal className="img-zoom overflow-hidden shadow-2xl">
                <img src={bazaar.lead} alt={bazaar.leadAlt} loading="lazy"
                  className="w-full h-[360px] md:h-[520px] object-cover" />
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <SectionLabel eyebrow={bazaar.eyebrow} title={bazaar.title} id="sb-bazaar" />
              <div className="rule-h max-w-[110px] my-6" />
              {bazaar.paragraphs.map((p, i) => (
                <p key={i} className="text-[#6b6459] leading-[1.95] text-[15px] mb-4">{p}</p>
              ))}
              <div className="grid grid-cols-2 gap-4 mt-6">
                {bazaar.side.map((im, i) => (
                  <Reveal key={i} delay={i * 120} className="img-zoom overflow-hidden shadow-lg">
                    <img src={im.src} alt={im.alt} loading="lazy" className="w-full h-36 md:h-44 object-cover" />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Saffron (dark, dramatic) */}
      <section aria-labelledby="sb-saffron" className="py-24 md:py-32 bg-[#221e18] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <Reveal className="clip-reveal">
              <img src={saffron.images[0].src} alt={saffron.images[0].alt} loading="lazy"
                className="w-full h-56 md:h-80 object-cover" />
            </Reveal>
            <Reveal delay={150} className="clip-reveal mt-8">
              <img src={saffron.images[1].src} alt={saffron.images[1].alt} loading="lazy"
                className="w-full h-56 md:h-80 object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-6">
            <SectionLabel eyebrow={saffron.eyebrow} title={saffron.title} id="sb-saffron" light />
            <div className="rule-h max-w-[110px] my-6" />
            {saffron.paragraphs.map((p, i) => (
              <p key={i} className="text-white/70 leading-[1.95] text-[15px] mb-4">{p}</p>
            ))}
          </div>
        </div>
      </section>

      {/* Lokum */}
      <section aria-labelledby="sb-lokum" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <SectionLabel eyebrow={lokum.eyebrow} title={lokum.title} id="sb-lokum" />
            <div className="rule-h max-w-[110px] my-6" />
            {lokum.paragraphs.map((p, i) => (
              <p key={i} className="text-[#6b6459] leading-[1.95] text-[15px] mb-4">{p}</p>
            ))}
          </div>
          <div className="lg:col-span-6 grid grid-cols-2 gap-4">
            <Reveal className="clip-reveal">
              <img src={lokum.images[0].src} alt={lokum.images[0].alt} loading="lazy"
                className="w-full h-64 md:h-80 object-cover" />
            </Reveal>
            <Reveal delay={150} className="clip-reveal mt-8">
              <img src={lokum.images[1].src} alt={lokum.images[1].alt} loading="lazy"
                className="w-full h-64 md:h-80 object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features — magazine asymmetric */}
      <section aria-labelledby="sb-features" className="py-24 md:py-32 bg-[#f6f0e6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="mb-14 md:mb-20">
            <p className="serif-i text-[#b8893a] text-2xl mb-2">Şehri Yaşayın</p>
            <h2 id="sb-features" className="font-display text-[2.6rem] md:text-6xl text-[#2b2620] font-500 leading-none">
              Gezilecek Yerler
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {/* Big feature */}
            <Reveal className="md:col-span-7">
              <article className="showcase group">
                <div className="showcase-img overflow-hidden">
                  <img src={features[0].img} alt={features[0].alt} loading="lazy"
                    className="w-full h-[340px] md:h-[560px] object-cover" />
                </div>
                <div className="flex items-start gap-4 mt-5">
                  <span className="index-num text-[#d9b877] text-4xl font-600 leading-none">{features[0].n}</span>
                  <div>
                    <h3 className="font-display text-3xl text-[#2b2620] font-600">{features[0].name}</h3>
                    <p className="text-[14px] text-[#6b6459] mt-1 max-w-md">{features[0].text}</p>
                  </div>
                </div>
              </article>
            </Reveal>

            {/* Two stacked features */}
            <div className="md:col-span-5 flex flex-col gap-6 md:gap-8">
              {features.slice(1).map((f, i) => (
                <Reveal key={i} delay={i * 120}>
                  <article className="showcase group">
                    <div className="showcase-img overflow-hidden">
                      <img src={f.img} alt={f.alt} loading="lazy"
                        className="w-full h-[240px] md:h-[256px] object-cover" />
                    </div>
                    <div className="flex items-start gap-4 mt-4">
                      <span className="index-num text-[#d9b877] text-3xl font-600 leading-none">{f.n}</span>
                      <div>
                        <h3 className="font-display text-2xl text-[#2b2620] font-600">{f.name}</h3>
                        <p className="text-[13px] text-[#6b6459] mt-1">{f.text}</p>
                      </div>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>

          {/* Nearby heritage & nature — verified real imagery (Wikimedia Commons) */}
          <div className="mt-16 md:mt-24 border-t border-[#e2d6bd] pt-14">
            <Reveal className="mb-10">
              <p className="serif-i text-[#b8893a] text-2xl mb-1">Şehrin Ötesinde</p>
              <h3 className="font-display text-3xl md:text-4xl text-[#2b2620] font-600 flex items-center gap-3">
                <Landmark size={22} className="text-[#b8893a]" /> Çevrede Keşfedilecekler
              </h3>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {nearby.map((n, i) => (
                <Reveal key={i} delay={i * 120}>
                  <article className="showcase group">
                    <div className="showcase-img overflow-hidden relative">
                      <img
                        src={n.img}
                        alt={n.alt}
                        loading="lazy"
                        className="w-full h-[300px] md:h-[380px] object-cover"
                      />
                      <span className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                      <span className="index-num absolute top-4 left-4 text-white/90 text-4xl font-600 leading-none">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <h4 className="absolute bottom-4 left-5 right-5 font-display text-2xl text-white font-600 flex items-center gap-2">
                        <MapPin size={16} className="text-[#d9b877] shrink-0" /> {n.name}
                      </h4>
                    </div>
                    <p className="text-[13.5px] text-[#6b6459] mt-4 leading-relaxed">{n.desc}</p>
                  </article>
                </Reveal>
              ))}
            </div>
            <p className="text-[11px] text-[#a89c86] mt-8 leading-relaxed max-w-3xl">{credits}</p>
          </div>
        </div>
      </section>

      {/* Transport */}
      <section aria-labelledby="sb-transport" className="py-20 bg-[#2b2620]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <Bus size={30} className="text-[#d9b877] mx-auto mb-5" />
            <h2 id="sb-transport" className="font-display text-3xl md:text-4xl text-white font-500 mb-3">
              {transport.title}
            </h2>
            <p className="text-white/55 text-[14px] max-w-xl mx-auto mb-9">{transport.note}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {transport.routes.map((r, i) => (
                <div key={i} className="border border-white/15 p-7">
                  <p className="font-display text-4xl text-[#d9b877] font-600">{r.time}</p>
                  <p className="text-white/60 text-[13px] mt-2 flex items-center justify-center gap-2">
                    <Clock size={14} /> {r.from}'dan kara yolu
                  </p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-28">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <p className="serif-i text-[#b8893a] text-2xl mb-4">Hikâyenin İçinde Konaklayın</p>
            <h2 className="font-display text-4xl md:text-5xl text-[#2b2620] font-500 leading-[1.05] mb-6">
              Bu şehrin kalbinde, tarihe misafir olun
            </h2>
            <div className="gold-divider mb-9" />
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => openReservation()} className="btn-gold px-9 py-4">
                Rezervasyon Yap
              </button>
              <Link to="/odalar" className="btn-outline-gold px-9 py-4 inline-flex items-center justify-center gap-2">
                Odalarımızı Görün <ArrowRight size={15} />
              </Link>
            </div>
            <p className="text-[13px] text-[#9a9086] mt-8">
              Sorularınız için <Link to="/iletisim" className="text-[#b8893a] underline underline-offset-4">iletişim</Link> sayfamızdan bize ulaşabilirsiniz.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Safranbolu;
